import {
  Calendar as CalendarIcon,
  Check,
  ChevronDown,
  Plus,
} from "@tamagui/lucide-icons";
import dayjs from "dayjs";
import { Parser } from "expr-eval";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { InputModeOptions, ModalProps } from "react-native";
import { FlatList } from "react-native";
import type { CalendarProps } from "react-native-calendars";
import type { MarkingProps } from "react-native-calendars/src/calendar/day/marking";
import {
  getTokens,
  getVariableValue,
  Label,
  Text,
  View,
  XStack,
  YStack,
} from "tamagui";
import type PlanModel from "../../plans/PlanModel";
import { StartOfWeek } from "../../plans/types";
import { Body } from "../../ui/Body";
import { Button } from "../../ui/Button";
import { Calendar } from "../../ui/Calendar";
import { Checkbox } from "../../ui/Checkbox";
import { EmojiPicker } from "../../ui/EmojiPicker";
import { FormInput } from "../../ui/FormInput";
import { FormLabel } from "../../ui/FormLabel";
import { Group } from "../../ui/Group";
import type { IconProp } from "../../ui/icons";
import { Modal } from "../../ui/Modal";
import { SelectForm } from "../../ui/SelectForm";
import { Separator } from "../../ui/Separator";
import { Sheet, type SheetProps } from "../../ui/Sheet";
import { TipsText } from "../../ui/TipsText";
import { Title } from "../../ui/Title";
import { getDayJsDateAndTimeWithFormat } from "../dayJs";
import { isIos } from "../utils";

export enum FormSheetType {
  Date = "date",
  Input = "input",
  Emoji = "emoji",
  Email = "email",
  Textarea = "textarea",
  Information = "information",
  Checkbox = "checkbox",
  Select = "select",
  SelectInline = "select_inline",
  Custom = "custom",
  Calculator = "calculator",
}

type FormSheetItemBase = {
  formKey: string;
  icon: IconProp;
  label?: string; // not used yet
  required?: boolean;
  description?: string; // not used yet
  placeholder?: string;
  infoText?: string;
  plan?: PlanModel; // to fetch settings
  isActiveBasedOnData?: (formData: FormValues) => boolean;
  // biome-ignore lint/suspicious/noExplicitAny: Because
  validator?: (value: any) => boolean;
};

type FormSheetItem = FormSheetItemBase & {
  type: Exclude<
    FormSheetType,
    | FormSheetType.Custom
    | FormSheetType.Select
    | FormSheetType.SelectInline
    | FormSheetType.Calculator
  >;
  inputMode?: InputModeOptions;
};

type CustomFormSheetItem = FormSheetItemBase & {
  type: FormSheetType.Custom;
  component: React.FC<CustomFormSheetInputProps>;
};

type SelectFormSheetItem = FormSheetItemBase & {
  type: FormSheetType.Select;
  options: { label: JSX.Element | string; value: unknown }[];
};

type SelectInlineFormSheetItem = FormSheetItemBase & {
  type: FormSheetType.SelectInline;
  options: { label: string; value: string }[];
};

type CalculatorFormSheetItem = FormSheetItemBase & {
  type: FormSheetType.Calculator;
  formatter: (value: number) => string;
};

export type FormSheetItems = (
  | FormSheetItem
  | CustomFormSheetItem
  | SelectFormSheetItem
  | SelectInlineFormSheetItem
  | CalculatorFormSheetItem
)[];

export type FormSheetInputPropsBase = {
  values: FormValues;
  errors: FormSheetItems;
  // biome-ignore lint/suspicious/noExplicitAny: Because
  setFormData: (data: any) => void;
};

export type FormSheetInputProps = FormSheetInputPropsBase & {
  item: FormSheetItem;
};

export type CalculatorFormSheetInputProps = FormSheetInputPropsBase & {
  item: CalculatorFormSheetItem;
};

export type CustomFormSheetInputProps = FormSheetInputPropsBase & {
  item: CustomFormSheetItem;
};

export type SelectFormSheetInputProps = FormSheetInputPropsBase & {
  item: SelectFormSheetItem;
};

const Select: React.FC<SelectFormSheetInputProps> = ({
  values,
  item,
  setFormData,
}) => {
  const options = item.options;
  const value = options.find((option) => values[item.formKey] === option.value);
  const [open, setOpen] = useState(false);

  const labelActive = !!value;

  return (
    <>
      <FormLabel
        label={item.placeholder}
        labelActive={labelActive}
        onLabelPress={() => setOpen(true)}
      >
        <XStack
          paddingHorizontal={23}
          paddingVertical={12}
          borderWidth={1}
          borderColor={"$divider"}
          flex={1}
          position="relative"
          alignItems="center"
          justifyContent="space-between"
          onPress={() => setOpen(true)}
        >
          <Body flex={1} muted={!value}>
            {value?.label}
          </Body>
          <ChevronDown />
        </XStack>
      </FormLabel>
      <Sheet
        sheetId="select_form"
        alwaysRender={false}
        open={open}
        onOpenChange={(open: boolean) => setOpen(open)}
        header={<Title>{item.placeholder}</Title>}
      >
        <FlatList
          data={options}
          keyExtractor={(data) => String(data.value)}
          ItemSeparatorComponent={Separator}
          contentContainerStyle={{
            height: 400,
          }}
          renderItem={(data) => (
            <XStack
              width="100%"
              alignItems="center"
              gap="$space.small"
              onPress={() => {
                setOpen(false);

                const newValues = {
                  ...values,
                  [item.formKey]: data.item.value,
                };
                setFormData(newValues);
              }}
              cursor="pointer"
            >
              {value?.value === data.item.value ? <Check /> : undefined}
              <Body>{data.item.label}</Body>
            </XStack>
          )}
        />
      </Sheet>
    </>
  );
};

export type SelectInlineFormSheetInputProps = FormSheetInputPropsBase & {
  item: SelectInlineFormSheetItem;
};

const SelectInline: React.FC<SelectInlineFormSheetInputProps> = ({
  values,
  item,
  setFormData,
}) => {
  const options = item.options;
  const value = options.find((option) => values[item.formKey] === option.value);

  const onChange = React.useCallback(
    (newValue: string) => {
      const newValues = {
        ...values,
        [item.formKey]: newValue,
      };
      setFormData(newValues);
    },
    [item, setFormData, values],
  );

  return (
    <SelectForm
      items={options}
      defaultValue={value?.value}
      onValueChange={onChange}
    />
  );
};

const FormSheetCalculator: React.FC<CalculatorFormSheetInputProps> = ({
  values,
  item,
  errors,
  setFormData,
}) => {
  const [sum, setSum] = useState(values[item.formKey] ?? 0);
  const handleChange = (text: string) => {
    try {
      const result = Parser.parse(text).evaluate();

      if (typeof result === "number") {
        setSum(result);
      }
    } catch (_e) {
      setSum(0);
    }

    const newValues = { ...values, [item.formKey]: text };
    setFormData(newValues);
  };

  const value = values[item.formKey];
  const showSum = !sum || sum === Number(value);

  return (
    <YStack flex={1} gap="$medium">
      <XStack width="100%">
        <FormInput
          keyboardType={isIos() ? "numbers-and-punctuation" : "numeric"}
          flex={1}
          defaultValue={`${value ?? ""}`}
          type="border"
          preventNewLine
          error={errors.find((e) => e.formKey === item.formKey) !== undefined}
          label={item.placeholder ?? ""}
          onChangeText={handleChange}
        />
        {!showSum && (
          <View
            paddingHorizontal={"$large"}
            backgroundColor={"$background"}
            justifyContent="center"
            alignItems="center"
            borderWidth={1}
            borderLeftWidth={0}
            borderColor="$divider"
          >
            <Body>{item.formatter(sum)}</Body>
          </View>
        )}
      </XStack>
      <TipsText
        text={
          "Enter calculations using basic math operators like addition (+), subtraction (-), multiplication (*), and division (/)"
        }
      />
    </YStack>
  );
};

const FormSheetInput: React.FC<FormSheetInputProps> = ({
  values,
  item,
  errors,
  setFormData,
}) => {
  return (
    <YStack flex={1} gap="$medium">
      <FormInput
        inputMode={item.inputMode}
        defaultValue={`${values[item.formKey] ?? ""}`}
        type="border"
        preventNewLine={item.type === FormSheetType.Input}
        error={errors.find((e) => e.formKey === item.formKey) !== undefined}
        minHeight={item.type === FormSheetType.Textarea ? 200 : undefined}
        label={item.placeholder ?? ""}
        onChangeText={(text) => {
          const newValues = { ...values, [item.formKey]: text };
          setFormData(newValues);
        }}
      />
      {item.infoText && <TipsText text={item.infoText} />}
    </YStack>
  );
};

export const DatePicker: React.FC<
  {
    plan?: PlanModel;
    value?: string | string[];
    onSelect?: (date: string) => void;
    header?: JSX.Element;
    footer?: JSX.Element;
    calendarProps?: CalendarProps;
  } & SheetProps
> = ({ onSelect, plan, value, calendarProps, ...sheetProps }) => {
  const tokens = getTokens();
  const selectedBackground = getVariableValue(tokens.color.$mutedBackground);

  const valueArr = typeof value === "string" ? [value] : value;

  const markedDates = useMemo(() => {
    const dates: { [key: string]: MarkingProps } = {};

    valueArr?.forEach((value) => {
      dates[value] = { selected: true, color: selectedBackground };
    });

    return dates;
  }, [selectedBackground, valueArr]);

  return (
    <Sheet alwaysRender={false} {...sheetProps}>
      <Calendar
        {...calendarProps}
        startOfWeek={plan?.data.settings.startOfWeek ?? StartOfWeek.Monday}
        onDayPress={(date) => {
          onSelect?.(date.dateString);
        }}
        markedDates={markedDates}
      />
    </Sheet>
  );
};

const FormSheetDatepicker: React.FC<FormSheetInputProps> = ({
  values,
  item,
  errors,
  setFormData,
}) => {
  const [sheetOpen, setSheetOpen] = useState(false);

  const value = values[item.formKey];
  let formattedDate = "";

  if (value) {
    const isValidDate = dayjs(value).isValid();

    if (isValidDate) {
      formattedDate = getDayJsDateAndTimeWithFormat(
        new Date(values[item.formKey]),
        item.plan?.data.settings.dateFormat.substring(0, 10) ?? "YYYY-MM-DD",
      );
    }

    formattedDate = value;
  }

  return (
    <>
      <FormInput
        defaultValue={formattedDate}
        type="border"
        preventNewLine={item.type === FormSheetType.Input}
        error={errors.find((e) => e.formKey === item.formKey) !== undefined}
        minHeight={item.type === FormSheetType.Textarea ? 200 : undefined}
        label={item.placeholder ?? ""}
        onChangeText={(text) => {
          const newValues = { ...values, [item.formKey]: text };
          setFormData(newValues);
        }}
        icon={CalendarIcon}
        onIconPress={() => setSheetOpen(true)}
      />
      <DatePicker
        sheetId="date_picker"
        plan={item.plan}
        value={String(values[item.formKey])}
        open={sheetOpen}
        onOpenChange={(open: boolean) => setSheetOpen(open)}
        onSelect={(date) => {
          const newValues = { ...values, [item.formKey]: date };
          setFormData(newValues);
          setSheetOpen(false);
        }}
      />
    </>
  );
};

const FormSheetCheckbox: React.FC<FormSheetInputProps> = ({
  values,
  item,
  setFormData,
}) => {
  return (
    <XStack
      gap="$small"
      alignItems="center"
      cursor="pointer"
      flex={1}
      onPress={(_e) => {
        const newValues = { ...values, [item.formKey]: !values[item.formKey] };
        setFormData(newValues);
      }}
    >
      <Checkbox checked={!!values[item.formKey]} />
      <Label>{item.label}</Label>
    </XStack>
  );
};

const FormSheetEmoji: React.FC<FormSheetInputProps> = ({
  values,
  item,
  setFormData,
}) => {
  const [emojiSheetOpen, setEmojiSheetOpen] = useState(false);
  const icon = values[item.formKey] ?? "Click to select icon";

  return (
    <XStack gap="$small" alignItems="center" cursor="pointer" flex={1}>
      <View
        onPress={() => setEmojiSheetOpen(true)}
        cursor="pointer"
        borderWidth={1}
        borderColor={"$divider"}
        backgroundColor={"$background"}
        alignItems="center"
        padding="$space.medium"
      >
        <Text lineHeight={24} fontSize={24}>
          {icon}
        </Text>
      </View>
      <EmojiPicker
        sheetId="emoji"
        hideSearch
        open={emojiSheetOpen}
        onOpenChange={(open: boolean) => setEmojiSheetOpen(open)}
        onSelect={(emoji) => {
          const newValues = { ...values, [item.formKey]: emoji.emoji };
          setFormData(newValues);
          setEmojiSheetOpen(false);
        }}
      />
    </XStack>
  );
};

const itemToInput: Record<
  Exclude<
    FormSheetType,
    | FormSheetType.Information
    | FormSheetType.Custom
    | FormSheetType.Select
    | FormSheetType.SelectInline
    | FormSheetType.Calculator
  >,
  React.FC<FormSheetInputProps>
> = {
  [FormSheetType.Date]: FormSheetDatepicker,
  [FormSheetType.Checkbox]: FormSheetCheckbox,
  [FormSheetType.Emoji]: FormSheetEmoji,
  [FormSheetType.Email]: FormSheetInput,
  [FormSheetType.Textarea]: FormSheetInput,
  [FormSheetType.Input]: FormSheetInput,
};

// biome-ignore lint/suspicious/noExplicitAny: Because
export type FormValues = Record<string, any>;

// biome-ignore lint/suspicious/noExplicitAny: Because
function convertDottedObject(obj: Record<string, any>): Record<string, any> {
  // biome-ignore lint/suspicious/noExplicitAny: Because
  const result: Record<string, any> = {};

  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const keys: string[] = key.split(".");
      // biome-ignore lint/suspicious/noExplicitAny: Because
      let nestedObj: Record<string, any> = result;

      for (let i = 0; i < keys.length; i++) {
        const nestedKey: string = keys[i];
        if (i === keys.length - 1) {
          nestedObj[nestedKey] = obj[key];
        } else {
          nestedObj[nestedKey] = nestedObj[nestedKey] || {};
          nestedObj = nestedObj[nestedKey];
        }
      }
    }
  }

  return result;
}

export type FormDataModalProps = ModalProps & {
  title: string | JSX.Element;
  icon?: IconProp;
  description?: string | JSX.Element;
  buttonSaveText?: string;
  buttonIcon?: IconProp;
  form: FormSheetItems;
  values: FormValues;
  onSave: (values: FormValues) => void;
  onClose: () => void;
  onValuesChange?: (props: {
    // biome-ignore lint/suspicious/noExplicitAny: Because
    setFormData: (data: any) => void;
    values: FormValues;
    // biome-ignore lint/suspicious/noExplicitAny: Because
    prevData: any;
  }) => void;
};

export const FormDataModal: React.FC<FormDataModalProps> = observer(
  ({
    title,
    icon,
    description,
    buttonSaveText,
    buttonIcon,
    values,
    form,
    onSave,
    onValuesChange,
    ...props
  }) => {
    const [formData, _setFormData] = React.useState(values);

    const setFormData = useCallback(
      // biome-ignore lint/suspicious/noExplicitAny: Because
      (data: any) => {
        _setFormData((prevData) => {
          // HACK TO MAKE ON VALUES CHANGE TRIGGER AFTER THIS UPDATE
          setTimeout(() => {
            onValuesChange?.({
              setFormData: _setFormData,
              values: data,
              prevData: prevData,
            });
          }, 50);

          return data;
        });
      },
      [onValuesChange],
    );

    useEffect(() => {
      _setFormData(values);
    }, [values]);

    const onCloseCallback = props.onClose;

    const onClose = useCallback(() => {
      setFormData(values); // reset to next open
      onCloseCallback();
    }, [setFormData, onCloseCallback, values]);

    const [errors, setErrors] = React.useState<FormSheetItems>([]);

    const validateForm = React.useCallback(() => {
      const errors: FormSheetItems = [];
      for (const f of form) {
        if (f.isActiveBasedOnData && !f.isActiveBasedOnData?.(formData)) {
          // Not visible right now
          continue;
        }

        if (f.validator && !f.validator(formData[f.formKey])) {
          errors.push(f);
        } else if (f.required && !formData[f.formKey]) {
          errors.push(f);
        } else if (!f.required && !formData[f.formKey]) {
          // let it pass
        } else {
          const v = formData[f.formKey];
          if (v !== null) {
            if (f.type === FormSheetType.Date) {
              const testDate = new Date(v as string);
              if (
                testDate instanceof Date &&
                !Number.isFinite(testDate.getTime())
              ) {
                errors.push(f);
              }
            }

            if (
              f.type === FormSheetType.Email &&
              String(v).match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) === null
            ) {
              errors.push(f);
            }
          }
        }
      }
      return errors;
    }, [form, formData]);

    const saveForm = React.useCallback(() => {
      const errors = validateForm();
      setErrors(errors);
      if (!errors.length) {
        onSave(convertDottedObject(formData));
        onClose();
      }
    }, [formData, onSave, validateForm, onClose]);

    const Icon = icon;

    return (
      <Modal
        modalId="data_form"
        {...props}
        header={
          <YStack gap="$small">
            <XStack gap="$medium" alignItems="center">
              <Title>{title}</Title>
              {Icon && <Icon />}
            </XStack>
            {!!description && <Body muted>{description}</Body>}
          </YStack>
        }
        footer={
          <Button
            onPress={saveForm}
            style={{ flex: 1, alignSelf: "stretch" }}
            icon={buttonIcon ?? Plus}
          >
            {buttonSaveText ?? "Save"}
          </Button>
        }
      >
        <Group borderBottom={false} borderTop={false}>
          {form.map((item) => {
            const Icon = item.icon;

            if (
              item.isActiveBasedOnData &&
              !item.isActiveBasedOnData?.(formData)
            ) {
              return null;
            }

            if (item.type === FormSheetType.Information) {
              if (!item.placeholder) {
                return null;
              }

              return (
                <XStack
                  key={item.formKey}
                  width={"100%"}
                  alignItems="center"
                  gap="$space.medium"
                >
                  <Icon />
                  <Body>{item.placeholder ?? ""}</Body>
                </XStack>
              );
            }

            let content: JSX.Element;
            if (item.type === FormSheetType.Custom) {
              const Component = item.component;
              content = (
                <Component
                  values={formData}
                  item={item}
                  errors={errors}
                  setFormData={setFormData}
                />
              );
            } else if (item.type === FormSheetType.Select) {
              content = (
                <Select
                  values={formData}
                  item={item}
                  errors={errors}
                  setFormData={setFormData}
                />
              );
            } else if (item.type === FormSheetType.SelectInline) {
              content = (
                <SelectInline
                  values={formData}
                  item={item}
                  errors={errors}
                  setFormData={setFormData}
                />
              );
            } else if (item.type === FormSheetType.Calculator) {
              content = (
                <FormSheetCalculator
                  values={formData}
                  item={item}
                  errors={errors}
                  setFormData={setFormData}
                />
              );
            } else {
              const InputComponent = itemToInput[item.type];
              content = (
                <InputComponent
                  values={formData}
                  item={item}
                  errors={errors}
                  setFormData={setFormData}
                />
              );
            }

            return (
              <XStack key={item.formKey} width={"100%"} gap="$space.medium">
                <View maxHeight={54} justifyContent="center">
                  <Icon />
                </View>
                {content}
              </XStack>
            );
          })}
        </Group>
      </Modal>
    );
  },
);
